import { format } from 'date-fns';

import { useParams } from '../../../contexts/RouterContext';
import { useReleaseGroupRelease } from '../../../hooks/api/releaseGroups/useReleaseGroupRelease';
import { useSelectedReleaseId } from '../../../redux/project_grouping/hooks';
import { toFiniteNumber } from '../../../utils';
import { isReleaseGroupScope } from '../types';
import type { ReportsV3Params } from '../types';

type SbomPublishState = {
  /** Whether the release's SBOM has finished publishing to the portal. */
  isPublished: boolean;
  /** Whether a publish is queued but not yet complete. */
  isPending: boolean;
  /** Human-readable time the SBOM was last published, or null if never. */
  lastPublishedAt: string | null;
};

/**
 * Reads the SBOM portal publish state of the selected release group release.
 * The release is undefined until it loads, and in project scope.
 */
export function useSbomPublishState(): SbomPublishState {
  const params = useParams<ReportsV3Params>();
  const isReleaseGroup = isReleaseGroupScope(params);
  const projectGroupId = isReleaseGroup ? toFiniteNumber(params.projectGroupId) : undefined;
  const selectedReleaseId = useSelectedReleaseId(projectGroupId);
  const { data: release } = useReleaseGroupRelease(
    projectGroupId ?? 0,
    selectedReleaseId,
  );

  //  The validity check guards against a malformed date, which date-fns would otherwise throw on.
  const publishedDate = release?.publishedAt ? new Date(release.publishedAt) : null;
  const lastPublishedAt = publishedDate && !Number.isNaN(publishedDate.getTime())
    ? format(publishedDate, 'MMMM do yyyy, h:mm:ss aaa')
    : null;

  return {
    isPublished: !!release?.publishedAt,
    isPending: release?.publishedOnPortal === 'pending',
    lastPublishedAt,
  };
}
